import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, ChartLine } from "@phosphor-icons/react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import FmsFormFull, { computeFmsTotals } from "../../components/FmsFormFull";

// ============================================================
// Yo-Yo Test niveles (del protocolo de recuperación intermitente nivel 1)
// Cada entrada = { level, shuttle, cumulative_m }
// ============================================================
const YOYO_LEVELS = [
  { level: 5, shuttle: 1, m: 40 },
  { level: 9, shuttle: 1, m: 80 },
  { level: 11, shuttle: 1, m: 120 }, { level: 11, shuttle: 2, m: 160 },
  { level: 12, shuttle: 1, m: 200 }, { level: 12, shuttle: 2, m: 240 }, { level: 12, shuttle: 3, m: 280 },
  { level: 13, shuttle: 1, m: 320 }, { level: 13, shuttle: 2, m: 360 }, { level: 13, shuttle: 3, m: 400 }, { level: 13, shuttle: 4, m: 440 },
  { level: 14, shuttle: 1, m: 480 }, { level: 14, shuttle: 2, m: 520 }, { level: 14, shuttle: 3, m: 560 }, { level: 14, shuttle: 4, m: 600 }, { level: 14, shuttle: 5, m: 640 }, { level: 14, shuttle: 6, m: 680 }, { level: 14, shuttle: 7, m: 720 }, { level: 14, shuttle: 8, m: 760 },
  { level: 15, shuttle: 1, m: 800 }, { level: 15, shuttle: 2, m: 840 }, { level: 15, shuttle: 3, m: 880 }, { level: 15, shuttle: 4, m: 920 }, { level: 15, shuttle: 5, m: 960 }, { level: 15, shuttle: 6, m: 1000 }, { level: 15, shuttle: 7, m: 1040 }, { level: 15, shuttle: 8, m: 1080 },
  { level: 16, shuttle: 1, m: 1120 }, { level: 16, shuttle: 2, m: 1160 }, { level: 16, shuttle: 3, m: 1200 }, { level: 16, shuttle: 4, m: 1240 }, { level: 16, shuttle: 5, m: 1280 }, { level: 16, shuttle: 6, m: 1320 }, { level: 16, shuttle: 7, m: 1360 }, { level: 16, shuttle: 8, m: 1400 },
  { level: 17, shuttle: 1, m: 1440 }, { level: 17, shuttle: 2, m: 1480 }, { level: 17, shuttle: 3, m: 1520 }, { level: 17, shuttle: 4, m: 1560 }, { level: 17, shuttle: 5, m: 1600 }, { level: 17, shuttle: 6, m: 1640 }, { level: 17, shuttle: 7, m: 1680 }, { level: 17, shuttle: 8, m: 1720 },
  { level: 18, shuttle: 1, m: 1760 }, { level: 18, shuttle: 2, m: 1800 }, { level: 18, shuttle: 3, m: 1840 }, { level: 18, shuttle: 4, m: 1880 }, { level: 18, shuttle: 5, m: 1920 }, { level: 18, shuttle: 6, m: 1960 }, { level: 18, shuttle: 7, m: 2000 }, { level: 18, shuttle: 8, m: 2040 },
  { level: 19, shuttle: 1, m: 2080 }, { level: 19, shuttle: 2, m: 2120 }, { level: 19, shuttle: 3, m: 2160 }, { level: 19, shuttle: 4, m: 2200 }, { level: 19, shuttle: 5, m: 2240 }, { level: 19, shuttle: 6, m: 2280 }, { level: 19, shuttle: 7, m: 2320 }, { level: 19, shuttle: 8, m: 2360 },
  { level: 20, shuttle: 1, m: 2400 }, { level: 20, shuttle: 2, m: 2440 }, { level: 20, shuttle: 3, m: 2480 }, { level: 20, shuttle: 4, m: 2520 }, { level: 20, shuttle: 5, m: 2560 }, { level: 20, shuttle: 6, m: 2600 }, { level: 20, shuttle: 7, m: 2640 }, { level: 20, shuttle: 8, m: 2680 },
  { level: 21, shuttle: 1, m: 2720 }, { level: 21, shuttle: 2, m: 2760 }, { level: 21, shuttle: 3, m: 2800 }, { level: 21, shuttle: 4, m: 2840 }, { level: 21, shuttle: 5, m: 2880 }, { level: 21, shuttle: 6, m: 2920 }, { level: 21, shuttle: 7, m: 2960 }, { level: 21, shuttle: 8, m: 3000 },
  { level: 22, shuttle: 1, m: 3040 }, { level: 22, shuttle: 2, m: 3080 }, { level: 22, shuttle: 3, m: 3120 }, { level: 22, shuttle: 4, m: 3160 }, { level: 22, shuttle: 5, m: 3200 }, { level: 22, shuttle: 6, m: 3240 }, { level: 22, shuttle: 7, m: 3280 }, { level: 22, shuttle: 8, m: 3320 },
  { level: 23, shuttle: 1, m: 3360 }, { level: 23, shuttle: 2, m: 3400 }, { level: 23, shuttle: 3, m: 3440 }, { level: 23, shuttle: 4, m: 3480 }, { level: 23, shuttle: 5, m: 3520 }, { level: 23, shuttle: 6, m: 3560 }, { level: 23, shuttle: 7, m: 3600 }, { level: 23, shuttle: 8, m: 3640 },
];

// Elasticity index colors (user-defined)
// verde: entre 10 y 20 · rojo: mayor a 20 · amarillo: menor a 10
function elasticityColor(v) {
  if (v == null || isNaN(v)) return "#A3A3A3";
  if (v > 20) return "#FF3B30";
  if (v >= 10) return "#32D74B";
  return "#FFD60A";
}

// Coordination index: escala de verdes cuanto más cerca de 100
// Usamos una función de similitud: 1 - min(|v-100|, 100)/100
function coordinationColor(v) {
  if (v == null || isNaN(v)) return "#A3A3A3";
  const dist = Math.min(Math.abs(v - 100), 100);
  const ratio = 1 - dist / 100; // 0 (lejos) → 1 (cerca de 100)
  // Interpolamos de verde oscuro #1a3a1f hasta verde brillante #32D74B
  const r = Math.round(26 + (50 - 26) * ratio);
  const g = Math.round(58 + (215 - 58) * ratio);
  const b = Math.round(31 + (75 - 31) * ratio);
  return `rgb(${r},${g},${b})`;
}

const TEST_TYPES = [
  {
    key: "yoyo",
    label: "Yo-Yo Test",
    description: "Recuperación intermitente nivel 1",
    higherBetter: true,
    chart: [
      { key: "distance_m", label: "Distancia (m)", color: "#FF3B30" },
      { key: "vo2max", label: "VO2 Max", color: "#32D74B" },
    ],
  },
  {
    key: "jumps",
    label: "Saltos (SJ, CMJ, Abalakov)",
    description: "Altura de salto para cada test",
    higherBetter: true,
    chart: [
      { key: "sj_cm", label: "SJ (cm)", color: "#FF3B30" },
      { key: "cmj_cm", label: "CMJ (cm)", color: "#007AFF" },
      { key: "abalakov_cm", label: "Abalakov (cm)", color: "#FFD60A" },
    ],
  },
  {
    key: "elasticity",
    label: "Índice de Elasticidad",
    description: "((CMJ − SJ) / SJ) × 100",
    higherBetter: null,
    chart: [{ key: "value", label: "Elasticidad (%)", color: "#32D74B" }],
  },
  {
    key: "coordination",
    label: "Índice de Coordinación",
    description: "((Abalakov − CMJ) / CMJ) × 100",
    higherBetter: null,
    chart: [{ key: "value", label: "Coordinación (%)", color: "#32D74B" }],
  },
  {
    key: "imtp",
    label: "IMTP",
    description: "Isometric Mid-Thigh Pull · asimetría L/R",
    higherBetter: true,
    chart: [
      { key: "peak_force_n", label: "Fuerza Pico (N)", color: "#FF3B30" },
      { key: "asymmetry_pct", label: "Asimetría (%)", color: "#FFD60A" },
    ],
  },
  {
    key: "t1000",
    label: "Test 1000m",
    description: "VFA = 1000 / tiempo (s)",
    higherBetter: false,
    chart: [
      { key: "time_seconds", label: "Tiempo (s)", color: "#FF3B30" },
      { key: "vfa", label: "VFA (m/s)", color: "#32D74B" },
    ],
  },
  {
    key: "speed",
    label: "Test de Velocidad",
    description: "Parciales en 10 / 20 / 30 / 40 m",
    higherBetter: false,
    chart: [
      { key: "d10", label: "10m", color: "#FF3B30" },
      { key: "d20", label: "20m", color: "#007AFF" },
      { key: "d30", label: "30m", color: "#FFD60A" },
      { key: "d40", label: "40m", color: "#32D74B" },
    ],
  },
  {
    key: "fms",
    label: "FMS",
    description: "Functional Movement Screen · 0 a 21",
    higherBetter: true,
    chart: [{ key: "total", label: "Total", color: "#FF3B30" }],
  },
];

// ============================================================
// Formularios por tipo — cada uno con sus cálculos reactivos
// ============================================================

function YoyoForm({ form, setForm }) {
  const options = YOYO_LEVELS;
  const selected = options.find((o) => `${o.level}-${o.shuttle}` === form.data.level_key);
  const cumulativeM = selected?.m ?? "";
  const vo2max = selected ? (selected.m * 0.0084 + 36.4).toFixed(1) : "";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-3">
        <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nivel alcanzado (estadio · parcial)</label>
        <select
          className="input-base"
          value={form.data.level_key || ""}
          onChange={(e) => {
            const key = e.target.value;
            const opt = options.find((o) => `${o.level}-${o.shuttle}` === key);
            setForm({
              ...form,
              data: {
                ...form.data,
                level_key: key,
                level: opt?.level ?? null,
                shuttle: opt?.shuttle ?? null,
                distance_m: opt?.m ?? null,
                vo2max: opt ? Number((opt.m * 0.0084 + 36.4).toFixed(1)) : null,
              },
            });
          }}
          required
          data-testid="yoyo-level-select"
        >
          <option value="">— Elegir nivel —</option>
          {options.map((o) => (
            <option key={`${o.level}-${o.shuttle}`} value={`${o.level}-${o.shuttle}`}>
              Nivel {o.level} · parcial {o.shuttle} · {o.m} m
            </option>
          ))}
        </select>
      </div>
      <Calc label="Distancia acumulada" value={cumulativeM !== "" ? `${cumulativeM} m` : "—"} color="#FF3B30" />
      <Calc label="VO2 Max estimado" value={vo2max !== "" ? `${vo2max} ml/kg/min` : "—"} color="#32D74B" hint="m × 0.0084 + 36.4" />
    </div>
  );
}

function JumpsForm({ form, setForm }) {
  const { sj_cm, cmj_cm, abalakov_cm } = form.data;
  const sj = Number(sj_cm), cmj = Number(cmj_cm), ab = Number(abalakov_cm);
  const elasticity = sj_cm && cmj_cm && sj > 0 ? ((cmj - sj) / sj) * 100 : null;
  const coordination = cmj_cm && abalakov_cm && cmj > 0 ? ((ab - cmj) / cmj) * 100 : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NumberField label="SJ · Squat Jump (cm)" value={sj_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, sj_cm: v } })} testid="jumps-sj" />
      <NumberField label="CMJ · Countermovement (cm)" value={cmj_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, cmj_cm: v } })} testid="jumps-cmj" />
      <NumberField label="Abalakov (cm)" value={abalakov_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, abalakov_cm: v } })} testid="jumps-abalakov" />
      <Calc label="Índice elasticidad" value={elasticity != null ? `${elasticity.toFixed(1)}%` : "—"} color={elasticityColor(elasticity)} hint="((CMJ − SJ) / SJ) × 100" />
      <Calc label="Índice coordinación" value={coordination != null ? `${coordination.toFixed(1)}%` : "—"} color={coordinationColor(coordination)} hint="((Ab − CMJ) / CMJ) × 100" />
    </div>
  );
}

function ElasticityForm({ form, setForm }) {
  const { sj_cm, cmj_cm } = form.data;
  const sj = Number(sj_cm), cmj = Number(cmj_cm);
  const value = sj_cm && cmj_cm && sj > 0 ? ((cmj - sj) / sj) * 100 : null;

  // sync computed value back into form.data so it's saved
  useEffect(() => {
    if (value != null && form.data.value !== Number(value.toFixed(2))) {
      setForm((f) => ({ ...f, data: { ...f.data, value: Number(value.toFixed(2)) } }));
    }
    // eslint-disable-next-line
  }, [sj_cm, cmj_cm]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NumberField label="SJ (cm)" value={sj_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, sj_cm: v } })} testid="elas-sj" />
      <NumberField label="CMJ (cm)" value={cmj_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, cmj_cm: v } })} testid="elas-cmj" />
      <Calc label="Índice elasticidad" value={value != null ? `${value.toFixed(1)}%` : "—"} color={elasticityColor(value)} hint="((CMJ − SJ) / SJ) × 100" />
    </div>
  );
}

function CoordinationForm({ form, setForm }) {
  const { cmj_cm, abalakov_cm } = form.data;
  const cmj = Number(cmj_cm), ab = Number(abalakov_cm);
  const value = cmj_cm && abalakov_cm && cmj > 0 ? ((ab - cmj) / cmj) * 100 : null;

  useEffect(() => {
    if (value != null && form.data.value !== Number(value.toFixed(2))) {
      setForm((f) => ({ ...f, data: { ...f.data, value: Number(value.toFixed(2)) } }));
    }
    // eslint-disable-next-line
  }, [cmj_cm, abalakov_cm]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NumberField label="CMJ (cm)" value={cmj_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, cmj_cm: v } })} testid="coord-cmj" />
      <NumberField label="Abalakov (cm)" value={abalakov_cm} onChange={(v) => setForm({ ...form, data: { ...form.data, abalakov_cm: v } })} testid="coord-abalakov" />
      <Calc label="Índice coordinación" value={value != null ? `${value.toFixed(1)}%` : "—"} color={coordinationColor(value)} hint="((Ab − CMJ) / CMJ) × 100" />
    </div>
  );
}

function ImtpForm({ form, setForm }) {
  const { force_right_n, force_left_n, peak_force_n } = form.data;
  const fr = Number(force_right_n), fl = Number(force_left_n);
  const asym = fr > 0 && fl > 0 ? (Math.abs(fr - fl) / Math.max(fr, fl)) * 100 : null;
  const peakCalc = fr && fl ? fr + fl : peak_force_n;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NumberField label="Fuerza Derecha (N)" value={force_right_n} onChange={(v) => setForm({ ...form, data: { ...form.data, force_right_n: v } })} testid="imtp-right" />
      <NumberField label="Fuerza Izquierda (N)" value={force_left_n} onChange={(v) => setForm({ ...form, data: { ...form.data, force_left_n: v } })} testid="imtp-left" />
      <NumberField label="Fuerza Pico total (N)" value={peakCalc || ""} onChange={(v) => setForm({ ...form, data: { ...form.data, peak_force_n: v } })} testid="imtp-peak" hint="Auto = D + I" />
      <NumberField label="Fuerza relativa (N/kg)" value={form.data.relative_force} onChange={(v) => setForm({ ...form, data: { ...form.data, relative_force: v } })} testid="imtp-rel" />
      <NumberField label="RFD 100ms (N/s)" value={form.data.rfd_100} onChange={(v) => setForm({ ...form, data: { ...form.data, rfd_100: v } })} testid="imtp-rfd" />
      <Calc label="Asimetría D/I" value={asym != null ? `${asym.toFixed(1)}%` : "—"} color={asym != null && asym > 10 ? "#FF3B30" : asym != null && asym > 5 ? "#FFD60A" : "#32D74B"} hint="|D − I| / max(D,I) × 100" />
    </div>
  );
}

function T1000Form({ form, setForm }) {
  const { time_seconds, avg_hr } = form.data;
  const t = Number(time_seconds);
  const vfa = t > 0 ? 1000 / t : null;

  useEffect(() => {
    if (vfa != null && form.data.vfa !== Number(vfa.toFixed(2))) {
      setForm((f) => ({ ...f, data: { ...f.data, vfa: Number(vfa.toFixed(2)) } }));
    }
    // eslint-disable-next-line
  }, [time_seconds]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NumberField label="Tiempo total (segundos)" value={time_seconds} onChange={(v) => setForm({ ...form, data: { ...form.data, time_seconds: v } })} testid="t1000-time" />
      <NumberField label="FC promedio (bpm)" value={avg_hr} onChange={(v) => setForm({ ...form, data: { ...form.data, avg_hr: v } })} testid="t1000-hr" />
      <Calc label="VFA · Vel. Final Aeróbica" value={vfa != null ? `${vfa.toFixed(2)} m/s` : "—"} color="#32D74B" hint="1000 / tiempo(s)" />
    </div>
  );
}

function SpeedForm({ form, setForm }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {["d10", "d20", "d30", "d40"].map((k, i) => (
        <NumberField key={k} label={`${(i + 1) * 10}m (s)`} value={form.data[k]} onChange={(v) => setForm({ ...form, data: { ...form.data, [k]: v } })} testid={`speed-${k}`} />
      ))}
    </div>
  );
}

function FmsForm({ form, setForm, sex }) {
  return (
    <FmsFormFull
      data={form.data}
      onChange={(newData) => {
        const totals = computeFmsTotals(newData, sex);
        setForm({ ...form, data: { ...newData, total: totals.total } });
      }}
      sex={sex || "MASCULINO"}
    />
  );
}

// Shared UI primitives
function NumberField({ label, value, onChange, testid, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted block mb-2">{label}</label>
      <input
        type="number"
        step="any"
        className="input-base"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-muted mt-1">{hint}</div>}
    </div>
  );
}

function Calc({ label, value, color, hint }) {
  return (
    <div className="bg-[#0A0A0A] border border-app p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      <div className="font-display font-black text-2xl" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-muted mt-1 italic">{hint}</div>}
    </div>
  );
}

// ============================================================
// Main component
// ============================================================

const FORM_COMPONENTS = {
  yoyo: YoyoForm,
  jumps: JumpsForm,
  elasticity: ElasticityForm,
  coordination: CoordinationForm,
  imtp: ImtpForm,
  t1000: T1000Form,
  speed: SpeedForm,
  fms: FmsForm,
};

export default function TestsTab({ userId, isAdmin, targetUser }) {
  const [activeType, setActiveType] = useState(TEST_TYPES[0].key);
  const [tests, setTests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), notes: "", data: {} });

  const currentDef = TEST_TYPES.find((t) => t.key === activeType);
  const FormComp = FORM_COMPONENTS[activeType];
  const sex = (targetUser?.profile?.sex || "MASCULINO").toUpperCase();

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/tests`, { params: { test_type: activeType } });
      setTests(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => {
    load();
    setShowForm(false);
    setForm({ date: new Date().toISOString().slice(0, 10), notes: "", data: {} });
    // eslint-disable-next-line
  }, [activeType, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Coerce numeric strings to numbers before saving
      const cleanData = Object.fromEntries(
        Object.entries(form.data).map(([k, v]) => {
          if (v === "" || v == null) return [k, null];
          const n = Number(v);
          return [k, isNaN(n) ? v : n];
        })
      );
      await api.post(`/users/${userId}/tests`, {
        test_type: activeType,
        date: form.date,
        data: cleanData,
        notes: form.notes,
      });
      toast.success("Testeo guardado");
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0, 10), notes: "", data: {} });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar testeo?")) return;
    try {
      await api.delete(`/tests/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-6" data-testid="test-type-tabs">
        {TEST_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveType(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition-colors ${
              activeType === t.key
                ? "border-[#FF3B30] text-white bg-[#1F1F1F]"
                : "border-[#262626] text-muted hover:text-white"
            }`}
            data-testid={`test-type-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-black uppercase text-2xl tracking-tighter">{currentDef.label}</h3>
          <div className="text-xs text-muted">{currentDef.description}</div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2" data-testid="new-test-btn">
            <Plus size={16} weight="bold" /> Nuevo testeo
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card-base p-6 mb-6" data-testid="test-form">
          <div className="mb-4 max-w-xs">
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Fecha</label>
            <input type="date" className="input-base" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="test-date" />
          </div>
          <FormComp form={form} setForm={setForm} sex={sex} />
          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Notas</label>
            <textarea className="input-base" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-3 mt-5">
            <button type="submit" className="btn-primary" data-testid="save-test-btn">Guardar testeo</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
          </div>
        </form>
      )}

      {tests.length === 0 ? (
        <div className="card-base p-12 text-center text-muted">No hay registros de {currentDef.label}</div>
      ) : (
        <>
          <EvolutionChart tests={tests} def={currentDef} />
          <TestsList tests={tests} type={activeType} isAdmin={isAdmin} onDelete={handleDelete} />
        </>
      )}
    </div>
  );
}

function TestsList({ tests, type, isAdmin, onDelete }) {
  return (
    <div className="space-y-3" data-testid="tests-list">
      {tests.map((t) => (
        <TestCard key={t.test_id} test={t} type={type} isAdmin={isAdmin} onDelete={onDelete} />
      ))}
    </div>
  );
}

function TestCard({ test, type, isAdmin, onDelete }) {
  const d = test.data || {};
  let content;
  switch (type) {
    case "yoyo":
      content = (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Nivel" value={d.level != null ? `${d.level}-${d.shuttle}` : "—"} />
          <Field label="Distancia" value={d.distance_m != null ? `${d.distance_m} m` : "—"} color="#FF3B30" />
          <Field label="VO2 Max" value={d.vo2max != null ? `${d.vo2max} ml/kg/min` : "—"} color="#32D74B" />
        </div>
      );
      break;
    case "jumps": {
      const sj = Number(d.sj_cm), cmj = Number(d.cmj_cm), ab = Number(d.abalakov_cm);
      const elas = d.sj_cm && d.cmj_cm && sj > 0 ? ((cmj - sj) / sj) * 100 : null;
      const coord = d.cmj_cm && d.abalakov_cm && cmj > 0 ? ((ab - cmj) / cmj) * 100 : null;
      content = (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Field label="SJ" value={d.sj_cm != null ? `${d.sj_cm} cm` : "—"} />
          <Field label="CMJ" value={d.cmj_cm != null ? `${d.cmj_cm} cm` : "—"} />
          <Field label="Abalakov" value={d.abalakov_cm != null ? `${d.abalakov_cm} cm` : "—"} />
          <Field label="Elasticidad" value={elas != null ? `${elas.toFixed(1)}%` : "—"} color={elasticityColor(elas)} />
          <Field label="Coordinación" value={coord != null ? `${coord.toFixed(1)}%` : "—"} color={coordinationColor(coord)} />
        </div>
      );
      break;
    }
    case "elasticity":
      content = (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="SJ" value={d.sj_cm != null ? `${d.sj_cm} cm` : "—"} />
          <Field label="CMJ" value={d.cmj_cm != null ? `${d.cmj_cm} cm` : "—"} />
          <Field label="Elasticidad" value={d.value != null ? `${Number(d.value).toFixed(1)}%` : "—"} color={elasticityColor(d.value)} />
        </div>
      );
      break;
    case "coordination":
      content = (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="CMJ" value={d.cmj_cm != null ? `${d.cmj_cm} cm` : "—"} />
          <Field label="Abalakov" value={d.abalakov_cm != null ? `${d.abalakov_cm} cm` : "—"} />
          <Field label="Coordinación" value={d.value != null ? `${Number(d.value).toFixed(1)}%` : "—"} color={coordinationColor(d.value)} />
        </div>
      );
      break;
    case "imtp": {
      const fr = Number(d.force_right_n), fl = Number(d.force_left_n);
      const asym = fr > 0 && fl > 0 ? (Math.abs(fr - fl) / Math.max(fr, fl)) * 100 : null;
      const asymColor = asym != null && asym > 10 ? "#FF3B30" : asym != null && asym > 5 ? "#FFD60A" : "#32D74B";
      content = (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="F. Derecha" value={d.force_right_n != null ? `${d.force_right_n} N` : "—"} />
          <Field label="F. Izquierda" value={d.force_left_n != null ? `${d.force_left_n} N` : "—"} />
          <Field label="F. Pico" value={d.peak_force_n != null ? `${d.peak_force_n} N` : "—"} color="#FF3B30" />
          <Field label="Asimetría" value={asym != null ? `${asym.toFixed(1)}%` : "—"} color={asymColor} />
          {d.relative_force != null && <Field label="F. relativa" value={`${d.relative_force} N/kg`} />}
          {d.rfd_100 != null && <Field label="RFD 100ms" value={`${d.rfd_100} N/s`} />}
        </div>
      );
      break;
    }
    case "t1000":
      content = (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Tiempo" value={d.time_seconds != null ? `${d.time_seconds} s` : "—"} color="#FF3B30" />
          <Field label="VFA" value={d.vfa != null ? `${d.vfa} m/s` : "—"} color="#32D74B" />
          {d.avg_hr != null && <Field label="FC prom" value={`${d.avg_hr} bpm`} />}
        </div>
      );
      break;
    case "speed":
      content = (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["d10", "d20", "d30", "d40"].map((k, i) => (
            <Field key={k} label={`${(i + 1) * 10}m`} value={d[k] != null ? `${d[k]} s` : "—"} />
          ))}
        </div>
      );
      break;
    case "fms": {
      // Si el testeo fue cargado con el nuevo flujo completo, calcular desglose; si no, mostrar total directo.
      const hasDetailed = d.deep_squat || d.hurdle_left || d.lunge_left || d.shoulder_left;
      const sex = d.sex || "MASCULINO";
      const t = hasDetailed ? computeFmsTotals(d, sex) : null;
      content = t ? (
        <div className="grid grid-cols-3 md:grid-cols-8 gap-3">
          <Field label="1. Empuje" value={`${t.deepSquat}/3`} />
          <Field label="2. Doble-simple" value={`${t.hurdle}/3`} />
          <Field label="3. Estocada" value={`${t.lunge}/3`} />
          <Field label="4. Hombro" value={`${t.shoulder}/3`} />
          <Field label="5. MMII" value={`${t.slr}/3`} />
          <Field label="6. Tronco" value={`${t.trunk}/3`} />
          <Field label="7. Rotacional" value={`${t.rotary}/3`} />
          <Field label="Total" value={`${t.total}/21`} color={t.total >= 14 ? "#32D74B" : t.total >= 11 ? "#FFD60A" : "#FF3B30"} />
        </div>
      ) : (
        <Field label="Total FMS" value={d.total != null ? `${d.total}/21` : "—"} color="#FF3B30" />
      );
      break;
    }
    default:
      content = null;
  }

  return (
    <div className="card-base p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
      <div className="flex-1">
        <div className="text-xs uppercase tracking-widest text-primary-app mb-3">{test.date}</div>
        {content}
        {test.notes && <div className="text-sm text-muted mt-3 italic">{test.notes}</div>}
      </div>
      {isAdmin && (
        <button onClick={() => onDelete(test.test_id)} className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30]" data-testid={`delete-test-${test.test_id}`}>
          <Trash size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}

function Field({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      <div className="font-display font-bold text-xl" style={{ color: color || "inherit" }}>{value}</div>
    </div>
  );
}

function EvolutionChart({ tests, def }) {
  const data = useMemo(() => {
    return [...tests]
      .filter((t) => def.chart.some((c) => t.data?.[c.key] != null && t.data[c.key] !== ""))
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
      .map((t) => {
        const row = { date: t.date };
        def.chart.forEach((c) => {
          const v = t.data?.[c.key];
          row[c.key] = v != null && v !== "" ? Number(v) : null;
        });
        return row;
      });
  }, [tests, def]);

  if (data.length < 2) {
    return (
      <div className="card-base p-6 mb-6" data-testid="test-chart-empty">
        <div className="flex items-center gap-2 mb-2">
          <ChartLine size={18} weight="bold" className="text-primary-app" />
          <h3 className="font-display font-bold uppercase text-lg tracking-tight">Evolución</h3>
        </div>
        <p className="text-muted text-sm">Cargá al menos 2 testeos con valores para ver el gráfico de evolución.</p>
      </div>
    );
  }

  return (
    <div className="card-base p-6 mb-6" data-testid="test-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="bold" className="text-primary-app" />
          <h3 className="font-display font-bold uppercase text-lg tracking-tight">Evolución</h3>
        </div>
        {def.higherBetter != null && (
          <span className="text-[10px] uppercase tracking-widest text-muted">
            {def.higherBetter ? "↑ Más alto es mejor" : "↓ Más bajo es mejor"}
          </span>
        )}
      </div>
      <div className="w-full" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#262626" strokeDasharray="2 4" />
            <XAxis dataKey="date" stroke="#A3A3A3" tick={{ fontSize: 11 }} />
            <YAxis stroke="#A3A3A3" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0, color: "#fff" }}
              labelStyle={{ color: "#A3A3A3", textTransform: "uppercase", letterSpacing: 2, fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }} />
            {def.chart.map((c) => (
              <Line
                key={c.key}
                type="monotone"
                dataKey={c.key}
                name={c.label}
                stroke={c.color}
                strokeWidth={2}
                dot={{ r: 4, fill: c.color }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
